import moment from 'moment';
import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

export type ComparisonRange = [moment.Moment, moment.Moment] | null | undefined;
export type SelectedRange = [moment.Moment | null, moment.Moment | null] | null | undefined;

export type PairComparisonInput<TRow> = {
	current: TRow[];
	previous: TRow[];
	hasComparison: boolean;
	selectedRange: SelectedRange;
	comparisonRange: ComparisonRange;
	/**
	 * Maps the matched previous-period row (or null when no match) and the current row to the
	 * shape that should be emitted for the previous series. The series field is appended after.
	 * Receives the raw previous row from the input — without the comparison series field — so the
	 * caller can spread it and add fields like `monthLabel: curRow.monthLabel`.
	 */
	buildPreviousRow: (previousRow: TRow | null, currentRow: TRow) => Record<string, any>;
};

export type PairComparisonResult = {
	current: any[];
	previous: any[];
};

/**
 * Pairs current-period chart rows with previous-period chart rows by `monthKey`, offsetting by
 * the calendar gap between the two range starts. Falls back to index-based pairing when no
 * range info is available.
 *
 * Returns both series tagged with the comparison series field. Callers decide whether to
 * prepend or append the previous series (BizCharts respects array order for z-index in some
 * geom configurations).
 *
 * Extracted from MetricChart and SpendChart in life_cycle_analytics_overview_page; previously
 * duplicated almost verbatim across both.
 */
export const pairComparisonByMonth = <TRow extends { monthKey?: string }>({
	current,
	previous,
	hasComparison,
	selectedRange,
	comparisonRange,
	buildPreviousRow,
}: PairComparisonInput<TRow>): PairComparisonResult => {
	const currentTagged = (current || []).map((row) => ({
		...row,
		[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
	}));

	if (!hasComparison || !previous || previous.length === 0) {
		return { current: currentTagged, previous: [] };
	}

	const previousByKey = new Map<string, TRow>();
	previous.forEach((row) => {
		if (row && row.monthKey) previousByKey.set(String(row.monthKey), row);
	});

	const monthOffset =
		selectedRange && comparisonRange && selectedRange[0] && comparisonRange[0]
			? selectedRange[0].diff(comparisonRange[0], 'months')
			: 0;

	const previousTagged = currentTagged.map((curRow: any, index: number) => {
		let prevRow: TRow | null = null;
		if (monthOffset > 0 && curRow && curRow.monthKey) {
			const curDate = moment(`${curRow.monthKey}-01`, 'YYYY-M-DD');
			const prevDate = curDate.clone().subtract(monthOffset, 'months');
			const expectedKey = `${prevDate.year()}-${prevDate.month() + 1}`;
			prevRow = previousByKey.get(expectedKey) || null;
		} else {
			prevRow = previous[index] || null;
		}
		return {
			...buildPreviousRow(prevRow, curRow),
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS,
		};
	});

	return { current: currentTagged, previous: previousTagged };
};
